import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, CheckCircle2, LinkIcon } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function TelegramConnectCard({ endpoint = "/telegram", statusEndpoint = "/telegram/status" }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(statusEndpoint);
      setStatus(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCode = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`${endpoint}/link`);
      setCode(data);
      toast.success(t("components.telegramconnectcard.link_code_generated_send_it_to_the_bot"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const unlink = async () => {
    try {
      await api.post(`${endpoint}/unlink`);
      setCode(null);
      await load();
      toast.success(t("components.telegramconnectcard.telegram_unlinked"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!status) return null;

  const configured = status.configured;
  const linked = status.linked;
  const botUsername = status.bot_username;

  return (
    <Card className="border-border" data-testid="telegram-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Send className="h-4 w-4 text-sky-500" /> Telegram notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!configured && (
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900" data-testid="telegram-not-configured">
            Telegram bot is not yet configured on the server. Ask your admin to set <code>TELEGRAM_BOT_TOKEN</code>.
          </div>
        )}
        {configured && linked && (
          <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-emerald-800" data-testid="telegram-linked">
            <CheckCircle2 className="h-4 w-4" /> Linked. You'll receive alerts here.
            <Button variant="outline" size="sm" className="ml-auto" onClick={unlink} data-testid="telegram-unlink-btn">{t("components.telegramconnectcard.unlink")}</Button>
          </div>
        )}
        {configured && !linked && (
          <>
            <p className="text-muted-foreground">
              Get alerts on Telegram for shortage warnings, credit receipts and daily summaries.
            </p>
            {!code ? (
              <Button onClick={requestCode} disabled={loading} data-testid="telegram-link-btn">
                <LinkIcon className="mr-1 h-3.5 w-3.5" /> {loading ? "Generating…" : "Connect Telegram"}
              </Button>
            ) : (
              <div className="rounded-md border bg-background p-3 text-xs" data-testid="telegram-code-box">
                <div className="mb-2 font-semibold text-foreground/80">{t("components.telegramconnectcard.send_this_to_our_bot_within_15_minutes")}</div>
                <div className="mb-2 rounded bg-card p-2 text-center font-mono text-lg font-bold tracking-wider" data-testid="telegram-code-value">
                  {code.code}
                </div>
                {botUsername && (
                  <a
                    href={`https://t.me/${botUsername}?start=${code.code}`}
                    target="_blank" rel="noreferrer"
                    className="block text-center font-semibold text-sky-600 hover:underline"
                    data-testid="telegram-open-bot"
                  >
                    Open Telegram bot &rarr;
                  </a>
                )}
                <div className="mt-2 text-muted-foreground">
                  If you already have the bot open, just type <code>{code.code}</code> and send.
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
